import { appBaseUrl } from "../audio/app-base-url";
import { NARRATION_LOCALES, narrationFile, type NarrationKey, type NarrationLocale } from "./keys";

/**
 * Voice narrator: a single voice at a time (the new one interrupts the previous),
 * with fallback to English if the locale's audio is missing and a "speaking" notice
 * so the app can lower the music (ducking).
 *
 * Plays with HTMLAudio: latency is not critical here and the files are
 * served as normal front assets.
 */

export interface Narrator {
  /** Speaks a key in the active locale. Resolves when finished (or on failure). */
  say: (key: NarrationKey) => Promise<void>;
  /** Cuts the current voice. */
  shutUp: () => void;
}

export interface NarratorOptions {
  /** Active locale (reactive — read on each `say`). */
  getLocale: () => string;
  /** Volume [0,1] (read on each `say`). */
  getVolume?: () => number;
  /** Speaking/silent notice for music ducking. */
  onSpeakingChange?: (speaking: boolean) => void;
  /** App base URL (by default the public base — see `appBaseUrl`). */
  baseUrl?: string;
}

function narrationLocale(locale: string): NarrationLocale {
  const found = NARRATION_LOCALES.find((code) => code === locale);
  return found ?? "en";
}

export function voiceUrl(locale: NarrationLocale, key: NarrationKey): string {
  return `audio/voice/${locale}/${narrationFile(key)}`;
}

export function createNarrator(options: NarratorOptions): Narrator {
  let current: HTMLAudioElement | null = null;

  const stop = () => {
    if (current) {
      current.pause();
      current = null;
      options.onSpeakingChange?.(false);
    }
  };

  const playUrl = async (url: string): Promise<void> =>
    new Promise((resolve, reject) => {
      // NOTE: do not use document.baseURI — in nested SPA routes it would point
      // to /games/... and the voice would resolve to a non-existent URL (see app-base-url).
      const base = options.baseUrl ?? appBaseUrl();
      const audio = new Audio(new URL(url, base).toString());
      audio.volume = options.getVolume?.() ?? 1;
      current = audio;
      audio.addEventListener("ended", () => {
        if (current === audio) {
          current = null;
          options.onSpeakingChange?.(false);
        }
        resolve();
      });
      audio.addEventListener("error", () => {
        if (current === audio) {
          current = null;
          options.onSpeakingChange?.(false);
        }
        reject(new Error(`voice asset missing: ${url}`));
      });
      options.onSpeakingChange?.(true);
      audio.play().catch(reject);
    });

  return {
    say: async (key) => {
      stop();
      const locale = narrationLocale(options.getLocale());
      try {
        await playUrl(voiceUrl(locale, key));
      } catch {
        if (locale === "en") {
          return; // no asset or fallback: silence, never crash
        }
        try {
          await playUrl(voiceUrl("en", key));
        } catch {
          // silence
        }
      }
    },
    shutUp: stop,
  };
}
